import GeoJSON from 'ol/format/GeoJSON';
import Feature from 'ol/Feature';
import type Geometry from 'ol/geom/Geometry';
import type { AppState, AttributeValue, EntityViewLevel, LayerModel, OriginalAttributes, VectorLayerModel } from '../core/types';
import { isPhysicalMeasureField } from '../domain/attributes';
import { deriveDisplaySet } from '../domain/displaySet';
import { entityPresentationGeometry, entityViewRows } from '../domain/entityView';

const LEVEL_NAME: Readonly<Record<EntityViewLevel, string>> = { finca: 'fincas', suerte: 'suertes', tablon: 'tablones', fragment: 'fragmentos' };
const TECHNICAL_KEY = /^(system|source|join|project):/;

export interface DataExportRecord { readonly geometry: Geometry | null; readonly properties: Readonly<Record<string, AttributeValue>> }
export interface DataExportSet { readonly name: string; readonly records: readonly DataExportRecord[]; readonly columns: readonly string[] }

/** Clean, human attribute names; at logical levels drop per-fragment physical values that vary. */
function exportProperties(model: LayerModel, level: EntityViewLevel, fragmentIds: readonly string[], attributes: OriginalAttributes): Record<string, AttributeValue> {
  const fragments = fragmentIds.flatMap((id) => { const fragment = model.fragmentsById.get(id); return fragment ? [fragment.attributes] : []; });
  return Object.fromEntries(Object.entries(attributes).filter(([key, value]) => {
    if (TECHNICAL_KEY.test(key) || value === undefined) return false;
    if (level === 'fragment' || !fragments.length || !(key in fragments[0]!)) return true;
    if (isPhysicalMeasureField(key)) return false;
    return fragments.every((item) => item[key] === fragments[0]![key]);
  }).map(([key, value]) => [key, value ?? null]));
}

/**
 * Entities visible at the active "Ver por" level, as real vectors with their attributes (source, Excel,
 * project and logical areas). Geometry comes from the active (edited) model; the source is untouched.
 */
export function masterExportSet(state: AppState): DataExportSet | null {
  const model = state.model; if (!model) return null;
  const level = state.entityViewLevel;
  const rows = entityViewRows(model, level, { allowedFragmentIds: deriveDisplaySet(state).masterFragmentIds, operationalFields: state.operationalFields, sourceModel: state.sourceModel });
  const records = rows.map((row): DataExportRecord => {
    const geometry = level === 'fragment' ? model.fragmentsById.get(row.id)?.feature.getGeometry()?.clone() ?? null : entityPresentationGeometry(model, row);
    const finca = model.fincas.get(row.ids.fincaId); const suerte = row.ids.suerteId ? model.suertes.get(row.ids.suerteId) : undefined; const tablon = row.ids.tablonId ? model.tablones.get(row.ids.tablonId) : undefined;
    const identity: Record<string, AttributeValue> = { entidad: row.label, nivel: level, finca: finca?.name ?? null, cod_finca: finca?.code ?? null, ...(suerte ? { suerte: suerte.label, cod_suerte: suerte.code } : {}), ...(tablon ? { tablon: tablon.code } : {}), area_activa_ha: Number(row.activeAreaHa.toFixed(4)), area_original_ha: Number(row.originalGeometryAreaHa.toFixed(4)), area_declarada_ha: row.declaredAreaHa ? Number(row.declaredAreaHa.toFixed(4)) : null };
    return { geometry, properties: { ...identity, ...exportProperties(model, level, row.fragmentIds, row.attributes) } };
  });
  return { name: `${model.name.replace(/\.[^.]+$/, '')}-${LEVEL_NAME[level]}`, records, columns: columnsOf(records) };
}

export function layerExportSet(state: AppState, layer: VectorLayerModel): DataExportSet {
  if (layer.kind === 'master') return masterExportSet({ ...state, entityViewLevel: state.entityViewLevel }) ?? { name: layer.name, records: [], columns: [] };
  const ids = deriveDisplaySet(state).layerFeatureIds.get(layer.id);
  const records = layer.features.filter(({ id }) => !ids || ids.has(id)).map((record): DataExportRecord => ({ geometry: record.feature.getGeometry()?.clone() ?? null, properties: { ...record.attributes } }));
  return { name: layer.name, records, columns: columnsOf(records) };
}

const columnsOf = (records: readonly DataExportRecord[]): readonly string[] => [...new Set(records.flatMap(({ properties }) => Object.keys(properties)))];

/** GeoJSON in WGS 84 (EPSG:4326), readable by QGIS and Google Earth converters. */
export function toGeoJson(set: DataExportSet, featureProjection = 'EPSG:3857'): string {
  const features = set.records.map(({ geometry, properties }) => { const feature = new Feature(properties); if (geometry) feature.setGeometry(geometry); return feature; });
  const collection = new GeoJSON().writeFeaturesObject(features, { featureProjection, dataProjection: 'EPSG:4326', decimals: 8 });
  return JSON.stringify({ ...collection, name: set.name });
}

// Spreadsheet formula injection (CSV injection): a text cell that starts with = + - @ (also after leading blanks or
// control characters, or in their full-width forms) or with a tab/CR may be evaluated by Excel/LibreOffice.
const FORMULA_START = /^[\s\u0000-\u001f\u007f-\u009f\u200b-\u200f\u2028\u2029\ufeff]*[=+\-@\uff1d\uff0b\uff0d\uff20]|^[\t\r]/;
const PLAIN_NUMBER = /^[+-]?(?:\d+(?:[.,]\d+)?|[.,]\d+)(?:[eE][+-]?\d+)?$/;
/** Text neutralised for spreadsheets with a leading apostrophe; numeric values and number-like text stay numbers. */
export const neutralizeSpreadsheetFormula = (text: string): string => FORMULA_START.test(text) && !PLAIN_NUMBER.test(text) ? `'${text}` : text;

const csvCell = (value: AttributeValue | undefined, protectFormulas = false): string => {
  if (value === null || value === undefined) return '';
  let text = typeof value === 'boolean' ? (value ? 'Sí' : 'No') : String(value);
  if (protectFormulas && typeof value === 'string') text = neutralizeSpreadsheetFormula(text);
  return /[",;\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export interface CsvOptions {
  /** Neutralise text cells a spreadsheet could evaluate as formulas. Off by default: the general exporter is unchanged. */
  readonly protectFormulas?: boolean;
}

/** UTF-8 CSV with BOM so Excel opens accents correctly; one row per visible entity. */
export function toCsv(set: DataExportSet, options: CsvOptions = {}): string {
  const cell = (value: AttributeValue | undefined): string => csvCell(value, options.protectFormulas);
  return `﻿${[set.columns.map(cell).join(','), ...set.records.map(({ properties }) => set.columns.map((column) => cell(properties[column])).join(','))].join('\r\n')}\r\n`;
}

export function safeFileName(name: string): string {
  return name.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').toLowerCase() || 'datos';
}
